import { FC, useCallback, useEffect } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeTypes,
  useEdgesState,
  useNodesState,
} from 'reactflow';

import { ErdEdgeData, ErdNodeData } from '../types';
import { ErdEntityNode } from './ErdEntityNode';
import { ErdRelationshipEdge } from './ErdRelationshipEdge';

import 'reactflow/dist/style.css';

const nodeTypes: NodeTypes = {
  erdEntity: ErdEntityNode,
};

const edgeTypes: EdgeTypes = {
  erdRelationship: ErdRelationshipEdge,
};

interface ErdGraphProps {
  nodes: Node<ErdNodeData>[];
  edges: Edge<ErdEdgeData>[];
  onPaneClick: () => void;
}

export const ErdGraph: FC<ErdGraphProps> = ({
  nodes: initialNodes,
  edges: initialEdges,
  onPaneClick,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const handlePaneClick = useCallback(() => {
    onPaneClick();
  }, [onPaneClick]);

  return (
    <div className="h-full w-full" data-testid="erd-graph">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        defaultEdgeOptions={{
          type: 'erdRelationship',
        }}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
};
